/** localStorage keys — admin vs company portals must not share one blob. */
export const AUTH_STORAGE_KEYS = Object.freeze({
  ADMIN_USER: "admin_user",
  COMPANY_USER: "company_user",
  CLIENT_USER: "client_user",
  /** @deprecated migrated into ADMIN_USER / COMPANY_USER */
  LEGACY_ADMIN: "admin",
});

export const SESSION_PORTAL_KEY = "sessionPortal";

export const getSessionPortalValue = () =>
  localStorage.getItem(SESSION_PORTAL_KEY) ||
  sessionStorage.getItem(SESSION_PORTAL_KEY);

/**
 * One-time migration: legacy single `admin` key → `admin_user` or `company_user`
 * based on `sessionPortal` (default admin).
 */
export function migrateLegacyAdminIfNeeded() {
  const legacy =
    localStorage.getItem(AUTH_STORAGE_KEYS.LEGACY_ADMIN) ||
    sessionStorage.getItem(AUTH_STORAGE_KEYS.LEGACY_ADMIN);
  if (!legacy) return;

  const portal = getSessionPortalValue();
  const targetKey = getAuthStorageKeyForPortal(portal);

  try {
    if (!localStorage.getItem(targetKey)) {
      localStorage.setItem(targetKey, legacy);
    }
  } catch {
    /* ignore */
  }
  localStorage.removeItem(AUTH_STORAGE_KEYS.LEGACY_ADMIN);
  sessionStorage.removeItem(AUTH_STORAGE_KEYS.LEGACY_ADMIN);
}

export function getAuthStorageKeyForPortal(portal) {
  switch (portal) {
    case "company":
      return AUTH_STORAGE_KEYS.COMPANY_USER;
    case "client":
      return AUTH_STORAGE_KEYS.CLIENT_USER;
    case "admin":
    default:
      return AUTH_STORAGE_KEYS.ADMIN_USER;
  }
}

export function readPersistedLoginForPortal(portal) {
  const key = getAuthStorageKeyForPortal(portal);
  try {
    const raw =
      localStorage.getItem(key) || sessionStorage.getItem(key) || "{}";
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

/** Hydrate Redux `auth.login` from the storage bucket matching `sessionPortal`. */
export function getInitialAuthLoginState() {
  migrateLegacyAdminIfNeeded();
  const portal = getSessionPortalValue();
  return readPersistedLoginForPortal(portal);
}

export function persistPortalLogin(portal, loginData) {
  const key = getAuthStorageKeyForPortal(portal);
  localStorage.setItem(key, JSON.stringify(loginData));
  localStorage.removeItem(AUTH_STORAGE_KEYS.LEGACY_ADMIN);
  sessionStorage.removeItem(AUTH_STORAGE_KEYS.LEGACY_ADMIN);
}

/** After successful login into `portalBeingActivated`, drop the other portal's snapshot. */
export function clearOppositePortalStorage(portalBeingActivated) {
  [
    AUTH_STORAGE_KEYS.ADMIN_USER,
    AUTH_STORAGE_KEYS.COMPANY_USER,
    AUTH_STORAGE_KEYS.CLIENT_USER,
  ].forEach((key) => {
    if (key === getAuthStorageKeyForPortal(portalBeingActivated)) return;
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  });
  localStorage.removeItem(AUTH_STORAGE_KEYS.LEGACY_ADMIN);
  sessionStorage.removeItem(AUTH_STORAGE_KEYS.LEGACY_ADMIN);
}

export function clearAllPortalLoginSnapshots() {
  localStorage.removeItem(AUTH_STORAGE_KEYS.ADMIN_USER);
  sessionStorage.removeItem(AUTH_STORAGE_KEYS.ADMIN_USER);
  localStorage.removeItem(AUTH_STORAGE_KEYS.COMPANY_USER);
  sessionStorage.removeItem(AUTH_STORAGE_KEYS.COMPANY_USER);
  localStorage.removeItem(AUTH_STORAGE_KEYS.CLIENT_USER);
  sessionStorage.removeItem(AUTH_STORAGE_KEYS.CLIENT_USER);
  localStorage.removeItem(AUTH_STORAGE_KEYS.LEGACY_ADMIN);
  sessionStorage.removeItem(AUTH_STORAGE_KEYS.LEGACY_ADMIN);
}
